import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Alert,
  Spinner,
} from "reactstrap";
import {
  mercantilScrapingRun,
  mercantilScrapingGetRun,
} from "../../helpers/backend_helper";
import { toast } from "react-toastify";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 min máximo de polling

/**
 * Modal de progreso del scraping de Mercantil Seguros.
 *
 * - Hace POST /api/v1/mercantil/scraping/run al iniciar.
 * - Recibe run_id y hace polling cada 5s a GET /api/v1/mercantil/scraping/runs/{run_id}.
 * - Muestra: listadas, creadas, omitidas, fallidas y detalle de fallos.
 * - Finaliza cuando finalizado !== null o tras 10 min.
 */
const MercantilScrapingModal = ({ isOpen, toggle }) => {
  const [phase, setPhase] = useState("idle"); // idle | running | done | error
  const [runId, setRunId] = useState(null);
  const [error, setError] = useState(null);
  const [runDoc, setRunDoc] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const startTimeRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearTimers();
    setPhase("idle");
    setRunId(null);
    setError(null);
    setRunDoc(null);
    setElapsedSec(0);
    startTimeRef.current = null;
  }, [clearTimers]);

  // Al abrir: iniciar scraping
  useEffect(() => {
    if (!isOpen) {
      // Al cerrar desde fuera, limpiar todo
      resetState();
      return;
    }

    // Ya arrancó? no re-disparar
    if (phase !== "idle") return;

    const start = async () => {
      setPhase("running");
      startTimeRef.current = Date.now();

      try {
        const res = await mercantilScrapingRun({});
        const data = res?.data;
        if (data?.status !== 202 || !data?.data?.run_id) {
          throw new Error(data?.message || "No se recibió run_id del backend");
        }
        const id = data.data.run_id;
        setRunId(id);

        // Timer de cuenta regresiva
        timerRef.current = setInterval(() => {
          const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
          setElapsedSec(elapsed);
          if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
            clearTimers();
            setPhase("error");
            setError("Tiempo de espera excedido. La corrida puede estar en curso en segundo plano. Consulte el histórico.");
          }
        }, 1000);

        // Polling cada 5s
        pollRef.current = setInterval(async () => {
          try {
            const pollRes = await mercantilScrapingGetRun(id);
            const doc = pollRes?.data?.data;
            if (!doc) return;
            setRunDoc(doc);

            // Si ya finalizó, detener polling
            if (doc.finalizado) {
              clearTimers();
              setPhase("done");
              if (doc.error_global) {
                setError(doc.error_global);
              }
            }
          } catch (e) {
            // Errores de polling no matan el polling; seguimos intentando
            // hasta el timeout global
          }
        }, POLL_INTERVAL_MS);
      } catch (e) {
        clearTimers();
        setPhase("error");
        setError(e?.response?.data?.detail || e?.message || "Error al iniciar scraping");
        toast.error("No se pudo iniciar la búsqueda en Mercantil");
      }
    };

    start();

    return () => clearTimers();
  }, [isOpen]);

  const handleClose = () => {
    clearTimers();
    toggle();
  };

  const isRunning = phase === "running";
  const isDone = phase === "done";
  const isError = phase === "error";
  const isSkippedByLock = runDoc?.saltada_por_lock === true;

  const total = runDoc?.total_listadas ?? 0;
  const creadas = runDoc?.total_creadas ?? 0;
  const omitidas = runDoc?.total_omitidas ?? 0;
  const fallidas = runDoc?.total_fallidas ?? 0;
  const progress = total > 0 ? Math.round(((creadas + omitidas + fallidas) / total) * 100) : (isRunning ? 0 : 100);

  const fallidasDetalle = (runDoc?.detalle || []).filter(
    (d) => d.estatus === "fallida"
  );

  const formattedElapsed = `${Math.floor(elapsedSec / 60)}:${String(
    elapsedSec % 60
  ).padStart(2, "0")}`;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered={true} size="lg">
      <ModalHeader toggle={handleClose}>
        <span className="fs-16 fw-semibold">
          <i className="ri-search-line me-2 text-primary"></i>
          Búsqueda de órdenes en Mercantil
        </span>
      </ModalHeader>
      <ModalBody className="py-4 px-4">
        {/* Estado: iniciando */}
        {isRunning && !runId && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-3 text-muted">Iniciando sesión con Mercantil…</p>
          </div>
        )}

        {/* Estado: running con run_id */}
        {isRunning && runId && (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="badge bg-info text-white">En curso</span>
              <span className="text-muted font-monospace">{formattedElapsed}</span>
            </div>
            <Progress value={progress} color="primary" className="mb-3" style={{ height: "8px" }} />
            <div className="row text-center g-2">
              <div className="col-3">
                <div className="fs-22 fw-semibold text-dark">{total}</div>
                <div className="text-muted fs-12">Listadas</div>
              </div>
              <div className="col-3">
                <div className="fs-22 fw-semibold text-success">{creadas}</div>
                <div className="text-muted fs-12">Creadas</div>
              </div>
              <div className="col-3">
                <div className="fs-22 fw-semibold text-warning">{omitidas}</div>
                <div className="text-muted fs-12">Omitidas</div>
              </div>
              <div className="col-3">
                <div className="fs-22 fw-semibold text-danger">{fallidas}</div>
                <div className="text-muted fs-12">Fallidas</div>
              </div>
            </div>
            {runDoc?.rango_desde && (
              <div className="mt-3 text-center text-muted fs-12">
                Rango: {runDoc.rango_desde} → {runDoc.rango_hasta}
              </div>
            )}
          </>
        )}

        {/* Estado: done */}
        {isDone && (
          <>
            {isSkippedByLock ? (
              <div className="text-center mb-3">
                <i className="ri-time-line fs-48 text-warning"></i>
                <h5 className="mt-2">Otra corrida está en ejecución</h5>
                <p className="text-muted fs-13">
                  El sistema detectó que ya hay una corrida de scraping activa.
                  Esta solicitud fue omitida para evitar solapamientos.
                </p>
              </div>
            ) : (
              <div className="text-center mb-3">
                <i
                  className={`ri-checkbox-circle-line fs-48 ${error ? "text-warning" : "text-success"}`}
                ></i>
                <h5 className="mt-2">
                  {error ? "Corrida completada con advertencias" : "Corrida completada"}
                </h5>
              </div>
            )}

            {error && (
              <Alert color="warning" className="fs-13">
                <strong>Advertencia:</strong> {error}
              </Alert>
            )}

            {!isSkippedByLock && (
              <div className="row text-center g-2 mb-3">
                <div className="col-3">
                  <div className="fs-22 fw-semibold text-dark">{total}</div>
                  <div className="text-muted fs-12">Listadas</div>
                </div>
                <div className="col-3">
                  <div className="fs-22 fw-semibold text-success">{creadas}</div>
                  <div className="text-muted fs-12">Creadas</div>
                </div>
                <div className="col-3">
                  <div className="fs-22 fw-semibold text-warning">{omitidas}</div>
                  <div className="text-muted fs-12">Omitidas</div>
                </div>
                <div className="col-3">
                  <div className="fs-22 fw-semibold text-danger">{fallidas}</div>
                  <div className="text-muted fs-12">Fallidas</div>
                </div>
              </div>
            )}

            {fallidasDetalle.length > 0 && !isSkippedByLock && (
              <div className="mt-3">
                <h6 className="text-danger">
                  <i className="ri-error-warning-line me-1"></i>
                  Órdenes fallidas ({fallidasDetalle.length})
                </h6>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Orden</th>
                        <th>Categoría</th>
                        <th>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fallidasDetalle.map((f) => (
                        <tr key={f.identificador}>
                          <td className="font-monospace">{f.identificador}</td>
                          <td>
                            <span className="badge bg-danger-subtle text-danger fs-10">
                              {f.error_categoria || "UNKNOWN"}
                            </span>
                          </td>
                          <td className="text-muted fs-12">{f.error_detalle || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {fallidasDetalle.length === 0 && creadas > 0 && !isSkippedByLock && (
              <Alert color="success" className="fs-13">
                Todas las órdenes fueron procesadas exitosamente.
              </Alert>
            )}
          </>
        )}

        {/* Estado: error */}
        {isError && (
          <div className="text-center py-3">
            <i className="ri-close-circle-line fs-48 text-danger"></i>
            <h5 className="mt-2 text-danger">Error</h5>
            <p className="text-muted">{error}</p>
          </div>
        )}

        {/* Footer botones */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-light"
            onClick={handleClose}
            disabled={isRunning && !runId}
          >
            {isRunning ? "Minimizar" : "Cerrar"}
          </button>
          {(isDone || isError) && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                resetState();
                setPhase("running");
                // Re-lanzar efecto manualmente
                mercantilScrapingRun({})
                  .then((res) => {
                    const data = res?.data;
                    if (data?.status !== 202 || !data?.data?.run_id) {
                      throw new Error("No se recibió run_id");
                    }
                    const id = data.data.run_id;
                    setRunId(id);
                    startTimeRef.current = Date.now();
                    timerRef.current = setInterval(() => {
                      const elapsed = Math.round(
                        (Date.now() - startTimeRef.current) / 1000
                      );
                      setElapsedSec(elapsed);
                      if (
                        Date.now() - startTimeRef.current >
                        MAX_POLL_DURATION_MS
                      ) {
                        clearTimers();
                        setPhase("error");
                        setError(
                          "Tiempo de espera excedido. Consulte el histórico."
                        );
                      }
                    }, 1000);
                    pollRef.current = setInterval(async () => {
                      try {
                        const pollRes = await mercantilScrapingGetRun(id);
                        const doc = pollRes?.data?.data;
                        if (!doc) return;
                        setRunDoc(doc);
                        if (doc.finalizado) {
                          clearTimers();
                          setPhase("done");
                          if (doc.error_global) setError(doc.error_global);
                        }
                      } catch (e) {
                        // seguir
                      }
                    }, POLL_INTERVAL_MS);
                  })
                  .catch((e) => {
                    clearTimers();
                    setPhase("error");
                    setError(
                      e?.response?.data?.detail ||
                        e?.message ||
                        "Error al reintentar"
                    );
                  });
              }}
            >
              <i className="ri-refresh-line me-1"></i>
              Reintentar
            </button>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

MercantilScrapingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

export default MercantilScrapingModal;
